/**
 * AlertRuleEditModal (C.3)
 *
 * Edit modal for adjusting alert rule severity, thresholds, and time windows.
 */

import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { useUpdateAlertRule } from '../../hooks/useSecurity';
import type { AlertRule } from '../../types/security.types';

const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;

interface AlertRuleEditModalProps {
  rule: AlertRule;
  onClose: () => void;
}

export function AlertRuleEditModal({ rule, onClose }: AlertRuleEditModalProps) {
  const updateRule = useUpdateAlertRule();
  const [severity, setSeverity] = useState(rule.severity);
  const [config, setConfig] = useState<Record<string, unknown>>({ ...(rule.condition_config || {}) });

  const handleSave = () => {
    updateRule.mutate(
      {
        ruleId: rule.rule_id,
        updates: {
          severity,
          condition_config: config,
        },
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  const updateConfig = (key: string, value: unknown) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Determine which config fields to show based on condition_type
  const configFields = getConfigFields(rule.condition_type, config);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ems-border">
          <h2 className="text-base font-semibold text-ems-text-primary">Edit Alert Rule</h2>
          <button onClick={onClose} className="p-1 hover:bg-ems-bg-card-hover rounded">
            <X className="w-4 h-4 text-ems-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Rule name (read-only) */}
          <div>
            <label className="block text-xs font-medium text-ems-text-secondary mb-1">Rule Name</label>
            <p className="text-sm font-medium text-ems-text-primary">{rule.name}</p>
          </div>

          {/* Category (read-only) */}
          <div>
            <label className="block text-xs font-medium text-ems-text-secondary mb-1">Category</label>
            <p className="text-xs font-mono text-ems-text-secondary">{rule.category}</p>
          </div>

          {/* Severity */}
          <div>
            <label className="block text-xs font-medium text-ems-text-secondary mb-1">Severity</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full border border-ems-border rounded-lg px-3 py-2 text-sm bg-ems-bg text-ems-text-primary focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
            >
              {SEVERITIES.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Dynamic config fields */}
          {configFields.map((field) => (
            <div key={field.key}>
              <label className="block text-xs font-medium text-ems-text-secondary mb-1">{field.label}</label>
              <input
                type="number"
                value={config[field.key] as number || field.default}
                onChange={(e) => updateConfig(field.key, parseFloat(e.target.value) || 0)}
                min={field.min}
                max={field.max}
                step={field.step || 1}
                className="w-full border border-ems-border rounded-lg px-3 py-2 text-sm bg-ems-bg text-ems-text-primary focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
              />
              {field.hint && <p className="text-[10px] text-ems-text-muted mt-0.5">{field.hint}</p>}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-ems-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-ems-text-secondary hover:bg-ems-bg-card-hover rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={updateRule.isPending}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ems-green bg-ems-forest border border-ems-green/20 hover:bg-ems-forest/80 rounded-lg transition-colors disabled:opacity-50"
          >
            {updateRule.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfigField {
  key: string;
  label: string;
  default: number;
  min: number;
  max: number;
  step?: number;
  hint?: string;
}

function getConfigFields(conditionType: string, config: Record<string, unknown>): ConfigField[] {
  switch (conditionType) {
    case 'lockout_config':
      return [
        { key: 'threshold', label: 'Failed Attempts Threshold', default: 5, min: 1, max: 100, hint: 'Lock account after this many failures' },
        { key: 'window_minutes', label: 'Window (minutes)', default: 15, min: 1, max: 1440, hint: 'Count failures within this window' },
        { key: 'auto_unlock_minutes', label: 'Auto Unlock (minutes)', default: 30, min: 1, max: 10080, hint: 'Auto-unlock after this duration' },
      ];

    case 'tunnel_failure':
      return [
        { key: 'mmsa_threshold', label: 'Min MMSA Count', default: 0, min: 0, max: 10, hint: 'Tunnel is down when MMSA count <= this value' },
      ];

    case 'tunnel_degraded':
      return [
        { key: 'packet_loss_threshold', label: 'Packet Loss Threshold (%)', default: 1, min: 0.1, max: 50, step: 0.1, hint: 'Alert when packet loss exceeds this percentage' },
      ];

    case 'routing_anomaly':
      return [
        { key: 'bgp_route_change_pct', label: 'BGP Route Change Threshold (%)', default: 50, min: 10, max: 100, hint: 'Alert when routes change by more than this percentage' },
      ];

    case 'off_hours_login':
      return [
        { key: 'off_hours_start', label: 'Business Hours Start (24h)', default: 6, min: 0, max: 23, hint: 'Logins before this hour trigger alert' },
        { key: 'off_hours_end', label: 'Business Hours End (24h)', default: 22, min: 1, max: 24, hint: 'Logins after this hour trigger alert' },
      ];

    case 'unusual_login_origin':
      return [
        { key: 'lookback_days', label: 'Lookback Period (days)', default: 30, min: 1, max: 365, hint: 'Check IP history over this many days' },
      ];

    default:
      // Generic: show any numeric values in condition_config
      return Object.entries(config)
        .filter(([, v]) => typeof v === 'number')
        .map(([key, v]) => ({
          key,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          default: v as number,
          min: 0,
          max: 99999,
        }));
  }
}
